<script setup>
/* eslint-disable no-alert */
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import AgentsAPI from 'dashboard/api/agents/agents';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
  // eslint-disable-next-line vue/no-unused-properties
  accountId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

// Track current agent - starts with props.agent, changes on transfer
const currentAgent = ref(null);
const originalAgent = ref(null);

const messages = ref([]);
const inputMessage = ref('');
const isLoading = ref(false);
// Token de sessão ESTÁVEL do teste — sem isso o agno cria sessão nova a cada
// msg e o agente esquece tudo (se reapresenta). Reinicia ao limpar a conversa.
const makeSessionToken = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `t${Math.floor(performance.now())}${Math.round(Math.random() * 1e9)}`;
const sessionToken = ref(makeSessionToken());
const loadingPhase = ref(0); // 0: thinking, 1: consulting, 2: processing
const loadingTimer = ref(null);
const messagesContainer = ref(null);
const inputRef = ref(null);
const fileInputRef = ref(null);
const pendingFiles = ref([]);

// Loading messages that change over time
const loadingMessage = computed(() => {
  const phases = [
    t('AI_AGENTS.PLAYGROUND.THINKING'),
    t('AI_AGENTS.PLAYGROUND.CONSULTING'),
    t('AI_AGENTS.PLAYGROUND.PROCESSING'),
  ];
  return phases[loadingPhase.value] || phases[0];
});

// Start loading with progressive messages
const startLoading = () => {
  isLoading.value = true;
  loadingPhase.value = 0;

  // After 2 seconds, show "Consulting..."
  loadingTimer.value = setTimeout(() => {
    loadingPhase.value = 1;
    // After 5 more seconds, show "Processing..."
    loadingTimer.value = setTimeout(() => {
      loadingPhase.value = 2;
    }, 5000);
  }, 2000);
};

// Stop loading and clear timers
const stopLoading = () => {
  isLoading.value = false;
  loadingPhase.value = 0;
  if (loadingTimer.value) {
    clearTimeout(loadingTimer.value);
    loadingTimer.value = null;
  }
};

// Initialize current agent
const initializeAgent = () => {
  currentAgent.value = { ...props.agent };
  originalAgent.value = { ...props.agent };
};

// Check if we're talking to a different agent than the original
const isTransferred = computed(
  () => currentAgent.value?.id !== originalAgent.value?.id
);

const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/ogg',
  'audio/webm',
  'video/mp4',
  'video/webm',
  'video/ogg',
  'application/pdf',
  'text/plain',
  'text/csv',
].join(',');

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const canSend = computed(() => {
  return (
    (inputMessage.value.trim().length > 0 || pendingFiles.value.length > 0) &&
    !isLoading.value
  );
});

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

const getFileIcon = file => {
  const type = file?.type || '';
  if (type.startsWith('image/')) return 'i-lucide-image';
  if (type.startsWith('audio/')) return 'i-lucide-mic';
  if (type.startsWith('video/')) return 'i-lucide-video';
  if (type === 'application/pdf') return 'i-lucide-file-text';
  return 'i-lucide-file';
};

const getFileType = file => {
  const type = file?.type || '';
  if (type.startsWith('audio/')) return 'audio';
  if (type.startsWith('video/')) return 'video';
  return 'document';
};

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const handleFileSelect = event => {
  const files = Array.from(event.target.files);

  files.forEach(file => {
    if (file.size > MAX_FILE_SIZE) {
      alert(`${file.name} excede o tamanho máximo de 20MB`);
      return;
    }

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => {
        pendingFiles.value.push({
          file,
          preview: e.target.result,
          type: 'image',
        });
      };
      reader.readAsDataURL(file);
    } else {
      pendingFiles.value.push({
        file,
        preview: null,
        type: getFileType(file),
      });
    }
  });

  // Reset input
  event.target.value = '';
};

const removeFile = index => {
  const file = pendingFiles.value[index];
  // Revoke object URL to prevent memory leaks
  if (file?.preview && file.preview.startsWith('blob:')) {
    URL.revokeObjectURL(file.preview);
  }
  pendingFiles.value.splice(index, 1);
};

const openFilePicker = () => {
  fileInputRef.value?.click();
};

const fileToBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const describeFiles = files => {
  const counts = {
    image: 0,
    audio: 0,
    video: 0,
    document: 0,
  };

  files.forEach(f => {
    const fileType = f.type || 'document';
    if (counts[fileType] !== undefined) {
      counts[fileType] += 1;
    }
  });

  const parts = [];
  if (counts.image > 0)
    parts.push(`${counts.image} imagem${counts.image > 1 ? 'ns' : ''}`);
  if (counts.audio > 0)
    parts.push(`${counts.audio} áudio${counts.audio > 1 ? 's' : ''}`);
  if (counts.video > 0)
    parts.push(`${counts.video} vídeo${counts.video > 1 ? 's' : ''}`);
  if (counts.document > 0)
    parts.push(`${counts.document} documento${counts.document > 1 ? 's' : ''}`);

  return `Enviado: ${parts.join(', ')}`;
};

const sendMessage = async () => {
  if (!canSend.value) return;

  const userMessage = inputMessage.value.trim();
  const filesToSend = [...pendingFiles.value];

  inputMessage.value = '';
  pendingFiles.value = [];

  // Build message object for display
  const userMsgObj = {
    id: Date.now(),
    type: 'user',
    content: userMessage,
    files: filesToSend.map(f => ({
      name: f.file.name,
      type: f.type,
      preview: f.preview,
      size: f.file.size,
    })),
    timestamp: new Date(),
  };

  messages.value.push(userMsgObj);
  scrollToBottom();

  // Show immediate acknowledgment message (so user doesn't feel ignored)
  const ackMsgId = Date.now() + 0.5;
  messages.value.push({
    id: ackMsgId,
    type: 'agent',
    content: t('AI_AGENTS.PLAYGROUND.PLEASE_WAIT'),
    timestamp: new Date(),
    agentName: currentAgent.value?.name,
    isAcknowledgment: true,
  });
  scrollToBottom();
  startLoading();

  try {
    // Prepare files for API
    const filesData = await Promise.all(
      filesToSend.map(async f => ({
        filename: f.file.name,
        content_type: f.file.type,
        data: await fileToBase64(f.file),
      }))
    );

    // Use currentAgent for the API call (may have changed due to transfer)
    const agentId = currentAgent.value?.id || props.agent.id;

    // Build context from conversation history
    // Filter out system messages (acknowledgment, transferring, etc.) and keep real conversation
    const conversationContext = messages.value
      .filter(
        m =>
          !m.isAcknowledgment &&
          !m.isTransferWait &&
          m.type !== 'transferring' &&
          m.type !== 'error' &&
          m.content // Only messages with actual content
      )
      .slice(-20) // Keep more context for better memory
      .map(m => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        content: m.content,
      }));

    const response = await AgentsAPI.testAgent(agentId, {
      message: userMessage || describeFiles(filesToSend),
      context: conversationContext,
      session_token: sessionToken.value,
      files: filesData.length > 0 ? filesData : undefined,
    });

    // Remove acknowledgment message before showing real response
    const ackIndex = messages.value.findIndex(m => m.id === ackMsgId);
    if (ackIndex > -1) {
      messages.value.splice(ackIndex, 1);
    }

    // Check if a transfer occurred
    if (response.data.transferred_to_agent) {
      const newAgent = response.data.transferred_to_agent;
      const previousAgentName = currentAgent.value?.name;

      // Step 1: Show "please wait" message from current agent
      messages.value.push({
        id: Date.now() + 1,
        type: 'agent',
        content: response.data.response,
        timestamp: new Date(),
        agentName: previousAgentName,
        isTransferWait: true,
      });
      scrollToBottom();

      // Step 2: Show "transferring..." indicator
      const transferringMsgId = Date.now() + 2;
      messages.value.push({
        id: transferringMsgId,
        type: 'transferring',
        targetAgentName: newAgent.name,
        timestamp: new Date(),
      });
      scrollToBottom();

      // Step 3: Wait 2.5 seconds to simulate transfer
      await new Promise(resolve => {
        setTimeout(resolve, 2500);
      });

      // Step 4: Remove transferring indicator
      const transferringIndex = messages.value.findIndex(
        m => m.id === transferringMsgId
      );
      if (transferringIndex > -1) {
        messages.value.splice(transferringIndex, 1);
      }

      // Step 5: Switch to the new agent
      currentAgent.value = newAgent;

      // Step 6: Show welcome message from new agent
      messages.value.push({
        id: Date.now() + 3,
        type: 'agent',
        content: newAgent.welcome_message,
        timestamp: new Date(),
        audioUrl: response.data.audio_url,
        agentName: newAgent.name,
        isTransfer: true,
      });
    } else {
      // Add regular agent response
      messages.value.push({
        id: Date.now() + 1,
        type: 'agent',
        content: response.data.response,
        timestamp: new Date(),
        audioUrl: response.data.audio_url,
        agentName: currentAgent.value?.name,
        toolsUsed: response.data.tools_used || [],
      });
    }
  } catch (error) {
    // Remove acknowledgment message on error
    const ackIndexErr = messages.value.findIndex(m => m.id === ackMsgId);
    if (ackIndexErr > -1) {
      messages.value.splice(ackIndexErr, 1);
    }

    messages.value.push({
      id: Date.now() + 1,
      type: 'error',
      content: error.response?.data?.error || t('AI_AGENTS.PLAYGROUND.ERROR'),
      timestamp: new Date(),
    });
  } finally {
    stopLoading();
    scrollToBottom();
    inputRef.value?.focus();
  }
};

const clearChat = () => {
  messages.value = [];
  // Nova sessão de teste = nova memória (zera o que o agente "lembrava").
  sessionToken.value = makeSessionToken();
  // Clean up any blob URLs
  pendingFiles.value.forEach(file => {
    if (file?.preview && file.preview.startsWith('blob:')) {
      URL.revokeObjectURL(file.preview);
    }
  });
  pendingFiles.value = [];
  // Reset to original agent
  currentAgent.value = { ...originalAgent.value };
};

// Reset to original agent (go back)
const resetToOriginalAgent = () => {
  currentAgent.value = { ...originalAgent.value };
};

const handleKeydown = event => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    sendMessage();
  }
};

// Record audio
const isRecording = ref(false);
const mediaRecorder = ref(null);
const audioChunks = ref([]);
const recordingStream = ref(null);

const startRecording = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    recordingStream.value = stream;

    // Try to use a more compatible format
    const getSupportedMimeType = () => {
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        return 'audio/webm;codecs=opus';
      }
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        return 'audio/webm';
      }
      return 'audio/mp4';
    };
    const mimeType = getSupportedMimeType();

    mediaRecorder.value = new MediaRecorder(stream, { mimeType });
    audioChunks.value = [];

    mediaRecorder.value.ondataavailable = event => {
      if (event.data && event.data.size > 0) {
        audioChunks.value.push(event.data);
      }
    };

    mediaRecorder.value.onstop = async () => {
      // Wait a bit to ensure all data is captured
      await new Promise(resolve => {
        setTimeout(resolve, 100);
      });

      if (audioChunks.value.length === 0) {
        return;
      }

      const audioBlob = new Blob(audioChunks.value, {
        type: mediaRecorder.value.mimeType,
      });

      // Determine file extension based on mime type
      const extension = mediaRecorder.value.mimeType.includes('webm')
        ? 'webm'
        : 'mp4';

      const file = new File([audioBlob], `audio_${Date.now()}.${extension}`, {
        type: mediaRecorder.value.mimeType,
      });

      // Convert blob to data URL for persistent preview
      const reader = new FileReader();
      reader.onload = async e => {
        pendingFiles.value.push({
          file,
          preview: e.target.result, // data:audio/webm;base64,...
          type: 'audio',
        });

        // Stop all tracks
        if (recordingStream.value) {
          recordingStream.value.getTracks().forEach(track => track.stop());
          recordingStream.value = null;
        }

        // Auto-send after recording
        await nextTick();
        sendMessage();
      };
      reader.readAsDataURL(audioBlob);
    };

    // Start recording with timeslice to ensure data is captured
    mediaRecorder.value.start(100);
    isRecording.value = true;
  } catch {
    // Microphone access denied or not available
  }
};

const stopRecording = () => {
  if (mediaRecorder.value && isRecording.value) {
    mediaRecorder.value.stop();
    isRecording.value = false;
  }
};

const toggleRecording = () => {
  if (isRecording.value) {
    stopRecording();
  } else {
    startRecording();
  }
};

// Initialize and focus input on mount or agent change
watch(
  () => props.agent,
  () => {
    initializeAgent();
    nextTick(() => inputRef.value?.focus());
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col h-full bg-n-solid-2 rounded-xl overflow-hidden">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-2">
        <div
          class="w-8 h-8 rounded-full flex items-center justify-center"
          :class="isTransferred ? 'bg-n-teal-3' : 'bg-n-brand/10'"
        >
          <span
            :class="isTransferred ? 'text-n-teal-11' : 'text-n-brand'"
            class="i-lucide-bot"
          />
        </div>
        <div>
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('AI_AGENTS.PLAYGROUND.TITLE') }}
          </h3>
          <div class="flex items-center gap-2">
            <p class="text-xs text-n-slate-10">
              {{ currentAgent?.name || agent.name }}
            </p>
            <span
              v-if="isTransferred"
              class="text-[10px] px-1.5 py-0.5 rounded bg-n-teal-3 text-n-teal-11"
            >
              {{ t('AI_AGENTS.PLAYGROUND.TRANSFERRED') }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-if="isTransferred"
          icon="i-lucide-undo-2"
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('AI_AGENTS.PLAYGROUND.BACK_TO_ORIGINAL')"
          @click="resetToOriginalAgent"
        />
        <Button
          v-if="messages.length > 0"
          icon="i-lucide-trash-2"
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('AI_AGENTS.PLAYGROUND.CLEAR')"
          @click="clearChat"
        />
      </div>
    </div>

    <!-- Messages Area -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-4">
      <!-- Empty State -->
      <div
        v-if="messages.length === 0"
        class="flex flex-col items-center justify-center h-full text-center"
      >
        <div
          class="w-16 h-16 rounded-full bg-n-alpha-2 flex items-center justify-center mb-4"
        >
          <span class="i-lucide-message-circle text-3xl text-n-slate-9" />
        </div>
        <h4 class="text-sm font-medium text-n-slate-11 mb-1">
          {{ t('AI_AGENTS.PLAYGROUND.EMPTY_TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-10 max-w-xs">
          {{ t('AI_AGENTS.PLAYGROUND.EMPTY_DESCRIPTION') }}
        </p>
        <div class="flex items-center gap-2 mt-4 text-xs text-n-slate-10">
          <span class="i-lucide-image w-4 h-4" />
          <span class="i-lucide-mic w-4 h-4" />
          <span class="i-lucide-video w-4 h-4" />
          <span class="i-lucide-file-text w-4 h-4" />
          <span>{{ t('AI_AGENTS.PLAYGROUND.MULTIMODAL_HINT') }}</span>
        </div>
      </div>

      <!-- Messages -->
      <div
        v-for="message in messages"
        :key="message.id"
        class="flex"
        :class="message.type === 'user' ? 'justify-end' : 'justify-start'"
      >
        <!-- Transfer in progress indicator -->
        <div
          v-if="message.type === 'transferring'"
          class="flex items-center gap-3 px-4 py-3 bg-n-amber-3 border border-n-amber-6 rounded-2xl"
        >
          <div class="relative">
            <span
              class="i-lucide-arrow-right-left w-5 h-5 text-n-amber-11 animate-pulse"
            />
          </div>
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-amber-11">
              {{ t('AI_AGENTS.PLAYGROUND.TRANSFERRING') }}
            </span>
            <span class="text-xs text-n-amber-10">
              {{ message.targetAgentName }}
            </span>
          </div>
          <Spinner size="small" class="ml-2" />
        </div>

        <!-- Regular message content -->
        <div v-else class="max-w-[80%]">
          <!-- Agent name label for agent messages -->
          <div
            v-if="message.type === 'agent' && message.agentName"
            class="flex items-center gap-1.5 mb-1"
          >
            <span class="i-lucide-bot w-3 h-3 text-n-slate-10" />
            <span class="text-xs text-n-slate-10">{{ message.agentName }}</span>
            <span
              v-if="message.isTransfer"
              class="i-lucide-sparkles w-3 h-3 text-n-teal-10"
            />
            <span
              v-if="message.isTransferWait"
              class="i-lucide-clock w-3 h-3 text-n-amber-10"
            />
            <Spinner v-if="message.isAcknowledgment" size="tiny" class="ml-1" />
          </div>

          <!-- Audio-only user message (no text) - show just the player without background -->
          <div
            v-if="
              message.type === 'user' &&
              !message.content &&
              message.files?.length === 1 &&
              (message.files[0].type === 'audio' ||
                message.files[0].name?.match(/\.(mp3|wav|ogg|webm|m4a)$/i))
            "
            class="flex items-center gap-2 p-2 bg-n-solid-3 rounded-2xl min-w-[220px] border border-n-weak"
          >
            <span class="i-lucide-mic w-5 h-5 text-n-brand shrink-0" />
            <audio
              :src="message.files[0].preview"
              controls
              class="flex-1 h-10"
              preload="metadata"
            />
          </div>

          <!-- Regular message with background -->
          <div
            v-else
            class="rounded-2xl px-4 py-2"
            :class="{
              'bg-n-brand text-white': message.type === 'user',
              'bg-n-solid-3 text-n-slate-12': message.type === 'agent',
              'bg-n-ruby-3 text-n-ruby-11': message.type === 'error',
            }"
          >
            <!-- Files preview (for user messages) -->
            <div
              v-if="message.files && message.files.length > 0"
              class="mb-2 space-y-2"
            >
              <div
                v-for="(file, idx) in message.files"
                :key="idx"
                class="rounded-lg overflow-hidden"
              >
                <!-- Image preview -->
                <img
                  v-if="file.type === 'image' && file.preview"
                  :src="file.preview"
                  :alt="file.name"
                  class="max-w-full max-h-48 rounded-lg"
                />
                <!-- Audio preview with simple player (like TTS response) -->
                <div
                  v-else-if="
                    (file.type === 'audio' ||
                      file.name?.match(/\.(mp3|wav|ogg|webm|m4a)$/i)) &&
                    file.preview
                  "
                  class="flex items-center gap-2 p-2 bg-white/10 rounded-lg min-w-[200px]"
                >
                  <span class="i-lucide-mic w-5 h-5 text-white/80 shrink-0" />
                  <audio
                    :src="file.preview"
                    controls
                    class="flex-1 h-10"
                    preload="metadata"
                  />
                </div>
                <!-- Other file types -->
                <div
                  v-else
                  class="flex items-center gap-2 p-2 bg-white/10 rounded-lg"
                >
                  <span
                    :class="getFileIcon({ type: file.type })"
                    class="w-5 h-5"
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-xs truncate">{{ file.name }}</p>
                    <p class="text-xs opacity-70">
                      {{ formatFileSize(file.size) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <p v-if="message.content" class="text-sm whitespace-pre-wrap">
              {{ message.content }}
            </p>

            <!-- Audio Player (if TTS is enabled) -->
            <audio
              v-if="message.audioUrl"
              :src="message.audioUrl"
              controls
              class="mt-2 w-full h-8"
            />

            <!-- Trace: ferramentas que o agente chamou -->
            <div
              v-if="message.toolsUsed && message.toolsUsed.length"
              class="mt-2 flex flex-wrap items-center gap-1 border-t border-n-weak pt-2"
            >
              <span class="i-lucide-wrench text-xs text-n-slate-10" />
              <span
                v-for="tool in message.toolsUsed"
                :key="tool"
                class="rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-[10px] font-medium text-n-slate-11"
              >
                {{ tool }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading indicator with progressive messages -->
      <div v-if="isLoading" class="flex justify-start">
        <div
          class="bg-n-solid-3 rounded-2xl px-4 py-3 flex items-center gap-2 transition-all"
        >
          <Spinner size="small" />
          <span class="text-sm text-n-slate-11">
            {{ loadingMessage }}
          </span>
        </div>
      </div>
    </div>

    <!-- Pending Files Preview -->
    <div
      v-if="pendingFiles.length > 0"
      class="px-4 py-2 border-t border-n-weak bg-n-solid-1"
    >
      <div class="flex flex-wrap gap-2">
        <div
          v-for="(item, index) in pendingFiles"
          :key="index"
          class="relative group"
        >
          <!-- Image preview -->
          <img
            v-if="item.type === 'image' && item.preview"
            :src="item.preview"
            class="w-16 h-16 object-cover rounded-lg border border-n-weak"
          />
          <!-- Audio preview with simple player -->
          <div
            v-else-if="
              item.type === 'audio' ||
              item.file?.name?.match(/\.(mp3|wav|ogg|webm|m4a)$/i)
            "
            class="flex items-center gap-2 p-2 bg-n-solid-3 rounded-lg border border-n-weak min-w-[200px]"
          >
            <span class="i-lucide-mic w-5 h-5 text-n-slate-11 shrink-0" />
            <audio
              v-if="item.preview"
              :src="item.preview"
              controls
              class="flex-1 h-10"
              preload="metadata"
            />
          </div>
          <!-- Other file types -->
          <div
            v-else
            class="w-16 h-16 flex flex-col items-center justify-center bg-n-solid-3 rounded-lg border border-n-weak"
          >
            <span
              :class="getFileIcon(item.file)"
              class="w-6 h-6 text-n-slate-11"
            />
            <span
              class="text-[10px] text-n-slate-10 mt-1 truncate max-w-14 px-1"
            >
              {{ item.file.name.split('.').pop() }}
            </span>
          </div>
          <!-- Remove button -->
          <button
            type="button"
            class="absolute -top-1 -right-1 w-5 h-5 bg-n-ruby-9 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            @click="removeFile(index)"
          >
            <span class="i-lucide-x w-3 h-3" />
          </button>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="p-4 border-t border-n-weak bg-n-solid-1">
      <div class="flex items-end gap-2">
        <!-- Hidden file input -->
        <input
          ref="fileInputRef"
          type="file"
          :accept="ACCEPTED_FILE_TYPES"
          multiple
          class="hidden"
          @change="handleFileSelect"
        />

        <!-- Attachment button -->
        <Button
          icon="i-lucide-paperclip"
          variant="ghost"
          color="slate"
          size="sm"
          :disabled="isLoading"
          @click="openFilePicker"
        />

        <!-- Record audio button -->
        <Button
          :icon="isRecording ? 'i-lucide-square' : 'i-lucide-mic'"
          variant="ghost"
          :color="isRecording ? 'ruby' : 'slate'"
          size="sm"
          :disabled="isLoading"
          :class="{ 'animate-pulse': isRecording }"
          @click="toggleRecording"
        />

        <textarea
          ref="inputRef"
          v-model="inputMessage"
          :placeholder="t('AI_AGENTS.PLAYGROUND.INPUT_PLACEHOLDER')"
          class="flex-1 px-4 py-3 text-sm bg-n-solid-3 border border-n-weak rounded-xl text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
          rows="1"
          :disabled="isLoading"
          @keydown="handleKeydown"
        />
        <Button
          icon="i-lucide-send"
          :disabled="!canSend"
          :is-loading="isLoading"
          @click="sendMessage"
        />
      </div>
      <p class="text-xs text-n-slate-10 mt-2 text-center">
        {{ t('AI_AGENTS.PLAYGROUND.HINT') }}
      </p>
    </div>
  </div>
</template>
